import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Copy, Download, ExternalLink, RefreshCw, Search, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/i18n';
import { fetchAffiliateConversion } from '@/services/productsService';
import {
  analyzeCatalog,
  downloadOperationsExport,
  filterOperationsRows,
  type OperationsIssue,
  type OperationsProduct,
} from '@/services/operationsMetrics';
import { fetchOperationsProducts } from '@/services/operationsService';

const ISSUE_OPTIONS: Array<OperationsIssue | 'all'> = [
  'all', 'missing_name', 'missing_brand', 'missing_link', 'missing_price',
  'missing_category', 'missing_image', 'invalid_price', 'duplicate_name',
  'duplicate_link', 'duplicate_order', 'missing_affiliates',
];

const ISSUE_LABELS: Record<OperationsIssue | 'all', string> = {
  all: 'operationsIssueAll',
  missing_name: 'operationsIssueMissingName',
  missing_brand: 'operationsIssueMissingBrand',
  missing_link: 'operationsIssueMissingLink',
  missing_price: 'operationsIssueMissingPrice',
  missing_category: 'operationsIssueMissingCategory',
  missing_image: 'operationsIssueMissingImage',
  invalid_price: 'operationsIssueInvalidPrice',
  duplicate_name: 'operationsIssueDuplicateName',
  duplicate_link: 'operationsIssueDuplicateLink',
  duplicate_order: 'operationsIssueDuplicateOrder',
  missing_affiliates: 'operationsIssueMissingAffiliates',
};

export default function Operations() {
  const { isOwner } = useAuth();
  const { t } = useLanguage();
  const [products, setProducts] = useState<OperationsProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [brand, setBrand] = useState('');
  const [category, setCategory] = useState('');
  const [minPrice, setMinPrice] = useState('');
  const [maxPrice, setMaxPrice] = useState('');
  const [issue, setIssue] = useState<OperationsIssue | 'all'>('all');
  const [conversionUrl, setConversionUrl] = useState('');
  const [conversionResult, setConversionResult] = useState<Record<string, string> | null>(null);
  const [converting, setConverting] = useState(false);

  const loadProducts = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setProducts(await fetchOperationsProducts());
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : t('operationsLoadError'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  const report = analyzeCatalog(products);
  const filteredRows = filterOperationsRows(report.rows, {
    query,
    brand,
    category,
    minPrice: minPrice === '' ? null : Number(minPrice),
    maxPrice: maxPrice === '' ? null : Number(maxPrice),
    issue,
  });
  const brands = [...new Set(products.map((row) => String(row.brand ?? '').trim()).filter(Boolean))].sort();
  const categories = [...new Set(products.map((row) => String(row.category ?? '').trim()).filter(Boolean))].sort();
  const issueRows = report.rows.filter((row) => row.issues.length > 0).length;

  const runConversion = async () => {
    if (!conversionUrl.trim()) return;
    setConverting(true);
    setConversionResult(null);
    try {
      const result = await fetchAffiliateConversion(conversionUrl);
      if (!Object.keys(result).length) throw new Error(t('operationsConvertEmpty'));
      setConversionResult(result);
    } catch (conversionError) {
      toast.error(conversionError instanceof Error ? conversionError.message : t('operationsConvertError'));
    } finally {
      setConverting(false);
    }
  };

  const copyConversion = async () => {
    if (!conversionResult) return;
    await navigator.clipboard.writeText(JSON.stringify(conversionResult, null, 2));
    toast.success(t('operationsCopied'));
  };

  const summaryCards = [
    [t('operationsTotalProducts'), report.total],
    [t('operationsProductsWithIssues'), issueRows],
    [t('operationsDuplicateLinks'), report.counts.duplicate_link],
    [t('operationsMissingAffiliates'), report.counts.missing_affiliates],
    [t('operationsMissingImages'), report.counts.missing_image],
    [t('operationsInvalidPrices'), report.counts.invalid_price],
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-6 w-6 text-primary" />
            <h1 className="text-2xl font-semibold tracking-tight">{t('operationsTitle')}</h1>
          </div>
          <p className="mt-1 text-sm text-muted-foreground">{t('operationsDescription')}</p>
        </div>
        <Button variant="outline" onClick={loadProducts} disabled={loading}>
          <RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          {t('operationsRefresh')}
        </Button>
      </div>

      {error && (
        <Card className="border-destructive/50">
          <CardContent className="flex items-center justify-between gap-4 pt-6">
            <p className="text-sm text-destructive">{error}</p>
            <Button size="sm" variant="outline" onClick={loadProducts}>{t('operationsRetry')}</Button>
          </CardContent>
        </Card>
      )}

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-6">
        {summaryCards.map(([label, value]) => (
          <Card key={String(label)}>
            <CardContent className="pt-5">
              <p className="text-2xl font-semibold">{loading ? '...' : value}</p>
              <p className="mt-1 text-xs text-muted-foreground">{label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{t('operationsExportTitle')}</CardTitle>
          <CardDescription>{t('operationsExportDescription')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-6">
            <div className="relative xl:col-span-2">
              <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input aria-label={t('operationsSearchPlaceholder')} value={query} onChange={(event) => setQuery(event.target.value)} placeholder={t('operationsSearchPlaceholder')} className="pl-9" />
            </div>
            <select aria-label={t('operationsAllBrands')} value={brand} onChange={(event) => setBrand(event.target.value)} className="h-10 rounded-md border border-input bg-background px-3 text-sm">
              <option value="">{t('operationsAllBrands')}</option>
              {brands.map((value) => <option key={value} value={value}>{value}</option>)}
            </select>
            <select aria-label={t('operationsAllCategories')} value={category} onChange={(event) => setCategory(event.target.value)} className="h-10 rounded-md border border-input bg-background px-3 text-sm">
              <option value="">{t('operationsAllCategories')}</option>
              {categories.map((value) => <option key={value} value={value}>{value}</option>)}
            </select>
            <Input aria-label={t('operationsMinPrice')} type="number" min="0" value={minPrice} onChange={(event) => setMinPrice(event.target.value)} placeholder={t('operationsMinPrice')} />
            <Input aria-label={t('operationsMaxPrice')} type="number" min="0" value={maxPrice} onChange={(event) => setMaxPrice(event.target.value)} placeholder={t('operationsMaxPrice')} />
          </div>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <select aria-label={t('operationsIssues')} value={issue} onChange={(event) => setIssue(event.target.value as OperationsIssue | 'all')} className="h-10 rounded-md border border-input bg-background px-3 text-sm sm:w-64">
              {ISSUE_OPTIONS.map((value) => <option key={value} value={value}>{t(ISSUE_LABELS[value])}</option>)}
            </select>
            <div className="flex flex-wrap items-center gap-2">
              <span className="mr-1 text-sm text-muted-foreground">{t('operationsMatched').replace('{count}', String(filteredRows.length))}</span>
              <Button variant="outline" disabled={!filteredRows.length} onClick={() => downloadOperationsExport(filteredRows, 'json')}>
                <Download className="mr-2 h-4 w-4" />JSON
              </Button>
              <Button variant="outline" disabled={!filteredRows.length} onClick={() => downloadOperationsExport(filteredRows, 'csv')}>
                <Download className="mr-2 h-4 w-4" />CSV
              </Button>
              <Button variant="outline" disabled={!filteredRows.length} onClick={() => downloadOperationsExport(filteredRows, 'xlsx')}>
                <Download className="mr-2 h-4 w-4" />XLSX
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('operationsIssuesTitle')}</CardTitle>
          <CardDescription>{t('operationsIssuesDescription')}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto rounded-md border">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-left">
                <tr>
                  <th className="px-4 py-3 font-medium">{t('operationsProduct')}</th>
                  <th className="px-4 py-3 font-medium">{t('operationsBrand')}</th>
                  <th className="px-4 py-3 font-medium">{t('operationsIssues')}</th>
                  {isOwner && <th className="px-4 py-3 text-right font-medium">{t('operationsAction')}</th>}
                </tr>
              </thead>
              <tbody>
                {filteredRows.slice(0, 100).map((row) => (
                  <tr key={row.id} className="border-t align-top">
                    <td className="px-4 py-3">
                      <div className="font-medium">{String(row.name || t('operationsUnnamed'))}</div>
                      <div className="mt-1 max-w-xs truncate text-xs text-muted-foreground">{row.id}</div>
                    </td>
                    <td className="px-4 py-3">{String(row.brand || '-')}</td>
                    <td className="px-4 py-3">
                      <div className="flex max-w-xl flex-wrap gap-1.5">
                        {row.issues.length
                          ? row.issues.map((rowIssue) => <Badge key={rowIssue} variant="secondary">{t(ISSUE_LABELS[rowIssue])}</Badge>)
                          : <Badge variant="outline">{t('operationsNoIssues')}</Badge>}
                      </div>
                    </td>
                    {isOwner && (
                      <td className="px-4 py-3 text-right">
                        <Button asChild variant="ghost" size="sm">
                          <Link to={`/admin/clothinglist/${row.id}`}>
                            {t('operationsEdit')}<ExternalLink className="ml-2 h-3.5 w-3.5" />
                          </Link>
                        </Button>
                      </td>
                    )}
                  </tr>
                ))}
                {!loading && filteredRows.length === 0 && (
                  <tr><td colSpan={isOwner ? 4 : 3} className="px-4 py-10 text-center text-muted-foreground">{t('operationsNoMatches')}</td></tr>
                )}
              </tbody>
            </table>
          </div>
          {filteredRows.length > 100 && <p className="mt-3 text-xs text-muted-foreground">{t('operationsTableLimit')}</p>}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('operationsConvertTitle')}</CardTitle>
          <CardDescription>{t('operationsConvertDescription')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col gap-2 sm:flex-row">
            <Input aria-label={t('operationsConvertTitle')} value={conversionUrl} onChange={(event) => setConversionUrl(event.target.value)} placeholder="https://weidian.com/item.html?itemID=..." />
            <Button onClick={runConversion} disabled={converting || !conversionUrl.trim()}>
              {converting ? t('operationsConverting') : t('operationsConvert')}
            </Button>
          </div>
          {conversionResult && (
            <div className="rounded-md border bg-muted/30 p-4">
              <div className="mb-3 flex items-center justify-between">
                <span className="text-sm font-medium">{t('operationsConvertResult')}</span>
                <Button variant="ghost" size="sm" onClick={copyConversion}><Copy className="mr-2 h-4 w-4" />{t('operationsCopy')}</Button>
              </div>
              <div className="space-y-2">
                {Object.entries(conversionResult).map(([key, value]) => (
                  <div key={key} className="grid gap-1 text-xs sm:grid-cols-[140px_1fr]">
                    <span className="font-medium">{key}</span>
                    <span className="break-all text-muted-foreground">{value}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
